import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import MonacoEditor from '@monaco-editor/react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Alert,
    Switch,
    FormControlLabel,
    Tabs,
    Tab,
    Skeleton,
    CircularProgress
} from '@mui/material';

// icons (@tabler/icons-react: guard against tree-shake or missing export giving undefined)
import * as TablerIcons from '@tabler/icons-react';
const IconKey = TablerIcons.IconKey;
const IconPlus = TablerIcons.IconPlus;
const IconEdit = TablerIcons.IconEdit;
const IconTrash = TablerIcons.IconTrash;
const IconCopy = TablerIcons.IconCopy;
const IconEye = TablerIcons.IconEye;
const IconEyeOff = TablerIcons.IconEyeOff;
const IconRefresh = TablerIcons.IconRefresh;
const IconWebhook = TablerIcons.IconWebhook;
const IconSettings = TablerIcons.IconSettings;
const IconChartLine = TablerIcons.IconChartLine;
const IconFileCode = TablerIcons.IconFileCode;
const IconRoute = TablerIcons.IconRoute;
const IconPlayerPlay = TablerIcons.IconPlayerPlay;
const IconRocket = TablerIcons.IconRocket;
const IconDeviceFloppy = TablerIcons.IconDeviceFloppy;
// Fallback nếu bất kỳ icon nào undefined (tránh lỗi "Element type is invalid... got: undefined")
const NoopIcon = () => null;
const IKey = IconKey || NoopIcon;
const IPlus = IconPlus || NoopIcon;
const IEdit = IconEdit || NoopIcon;
const ITrash = IconTrash || NoopIcon;
const ICopy = IconCopy || NoopIcon;
const IEye = IconEye || NoopIcon;
const IEyeOff = IconEyeOff || NoopIcon;
const IRefresh = IconRefresh || NoopIcon;
const IWebhook = IconWebhook || NoopIcon;
const ISettings = IconSettings || NoopIcon;
const IChartLine = IconChartLine || NoopIcon;
const IFileCode = IconFileCode || NoopIcon;
const IRoute = IconRoute || NoopIcon;
const IPlayerPlay = IconPlayerPlay || NoopIcon;
const IRocket = IconRocket || NoopIcon;
const IDeviceFloppy = IconDeviceFloppy || NoopIcon;

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { openSnackbar } from 'store/slices/snackbar';
import apiSlice from 'store/slices/apiSlice';

// ==============================|| PROJECT API GATEWAY ||============================== //

const ProjectApiGateway = ({ projectName, projectSubname, userToken, onClose }) => {
    const handleBack = () => {
        if (onClose) onClose();
    };
    const theme = useTheme();
    const dispatch = useDispatch();

    const { data: apiKeysData, isLoading: loadingApiKeys, refetch: refetchApiKeys } = apiSlice.useGetProjectApiKeysQuery(
        { projectName, user_token: userToken || undefined },
        { skip: !projectName }
    );
    const { data: projectMetricsData, isLoading: loadingMetrics, refetch: refetchMetrics } = apiSlice.useGetProjectApiGatewayMetricsQuery(
        { projectName, user_token: userToken || undefined },
        { skip: !projectName }
    );
    const [createApiKey, { isLoading: creating }] = apiSlice.useCreateProjectApiKeyMutation();
    const [deleteApiKey, { isLoading: deleting }] = apiSlice.useDeleteProjectApiKeyMutation();

    // API Hub: services, routes, test, deploy (JWT)
    const { data: servicesData, isLoading: loadingServices, refetch: refetchServices } = apiSlice.useGetApihubServicesQuery(projectName, { skip: !projectName });
    const { data: routesData, isLoading: loadingRoutes, refetch: refetchRoutes } = apiSlice.useGetApihubRoutesQuery(projectName, { skip: !projectName });
    const { data: deployStatusData, isLoading: loadingDeploy, refetch: refetchDeploy } = apiSlice.useGetApihubDeployStatusQuery(projectName, { skip: !projectName });
    const [uploadService, { isLoading: uploadingService }] = apiSlice.useUploadApihubServiceMutation();
    const [deleteService, { isLoading: deletingService }] = apiSlice.useDeleteApihubServiceMutation();
    const [createRoute, { isLoading: creatingRoute }] = apiSlice.useCreateApihubRouteMutation();
    const [updateRoute, { isLoading: updatingRoute }] = apiSlice.useUpdateApihubRouteMutation();
    const [deleteRoute, { isLoading: deletingRoute }] = apiSlice.useDeleteApihubRouteMutation();
    const [updateApihubServiceContent, { isLoading: savingServiceContent }] = apiSlice.useUpdateApihubServiceContentMutation();
    const [putApihubRoutesFull, { isLoading: savingRoutesFull }] = apiSlice.usePutApihubRoutesFullMutation();
    const [testEndpoint, { isLoading: testing }] = apiSlice.useTestApihubEndpointMutation();
    const [deploy, { isLoading: deploying }] = apiSlice.useDeployApihubMutation();
    const [undeploy, { isLoading: undeploying }] = apiSlice.useUndeployApihubMutation();
    const [seedSamples, { isLoading: seedingSamples }] = apiSlice.useSeedApihubSamplesMutation();
    const [syncSamples, { isLoading: syncingSamples }] = apiSlice.useSyncApihubSamplesMutation();
    const [createServiceFromTemplate, { isLoading: creatingFromTemplate }] = apiSlice.useCreateApihubServiceFromTemplateMutation();

    const [tabValue, setTabValue] = useState(0);
    const [routeDialog, setRouteDialog] = useState({ open: false, edit: null });
    const [routeForm, setRouteForm] = useState({ path: '', method: 'POST', service_file: '', entrypoint: 'run', param_mapping: {}, enabled: true, action_type: 'standard', params_example: '' });
    const [testForm, setTestForm] = useState({ endpoint_id: '', params: '{}', selected_key_id: '' });
    const [testUploadFiles, setTestUploadFiles] = useState([]); // File[] khi test endpoint upload
    const [testResult, setTestResult] = useState(null);
    const [createServiceDialogOpen, setCreateServiceDialogOpen] = useState(false);
    const [createServiceTemplate, setCreateServiceTemplate] = useState('POST');
    const [createServiceFilename, setCreateServiceFilename] = useState('');
    // Key vừa tạo (full key) để dùng trong bảng curl ở tab Deploy
    const [lastCreatedApiKey, setLastCreatedApiKey] = useState(null);
    const [deployCurlDialogOpen, setDeployCurlDialogOpen] = useState(false);
    const [deployCurlSelectedKeyId, setDeployCurlSelectedKeyId] = useState('');
    const [deployCurlTableVisible, setDeployCurlTableVisible] = useState(false);
    const [deployCurlFetchedKey, setDeployCurlFetchedKey] = useState('');
    const [fetchKeyForCurl, { isLoading: loadingKeyPlaintext }] = apiSlice.useLazyGetProjectApiKeyPlaintextQuery();
    const deployCurlCopyRef = useRef(null);
    // Monaco: service editor (filename or null), local content — khai báo trước khi dùng trong query
    const [serviceEditorFile, setServiceEditorFile] = useState(null);
    const [serviceEditorContent, setServiceEditorContent] = useState('');
    // Monaco: routes JSON editor
    const [routesJsonOpen, setRoutesJsonOpen] = useState(false);
    const [routesJsonContent, setRoutesJsonContent] = useState('[]');

    const { data: serviceContentData, isLoading: loadingServiceContent } = apiSlice.useGetApihubServiceContentQuery(
        { projectName, filename: serviceEditorFile },
        { skip: !projectName || !serviceEditorFile }
    );
    useEffect(() => {
        if (serviceEditorFile && serviceContentData?.name === serviceEditorFile) {
            setServiceEditorContent(serviceContentData.content ?? '');
        }
    }, [serviceEditorFile, serviceContentData]);
    useEffect(() => {
        if (routesJsonOpen && routesData?.endpoints) {
            setRoutesJsonContent(JSON.stringify(routesData.endpoints, null, 2));
        }
    }, [routesJsonOpen, routesData?.endpoints]);

    const apiKeys = apiKeysData?.api_keys ?? [];
    const webhooks = apiKeysData?.webhooks ?? [];
    const projectMetrics = projectMetricsData
        ? {
              totalRequests: projectMetricsData.requestsLast24h ?? 0,
              requestsLast7d: projectMetricsData.requestsLast7d ?? 0,
              requestsLast30d: projectMetricsData.requestsLast30d ?? 0,
              successRate: projectMetricsData.successRate ?? 0,
              errorRate: projectMetricsData.errorRate ?? 0,
              avgResponseTime: projectMetricsData.avgResponseTime ?? 0,
              activeKeys: projectMetricsData.activeKeys ?? 0,
              topEndpoints: projectMetricsData.topEndpoints ?? []
          }
        : { totalRequests: 0, requestsLast7d: 0, requestsLast30d: 0, successRate: 0, errorRate: 0, avgResponseTime: 0, activeKeys: 0, topEndpoints: [] };

    const effectiveTestApiKey = (() => {
        const k = apiKeys.find((x) => x.id === testForm.selected_key_id);
        return (k && k.key && !k.key.includes('••••••••')) ? k.key : '';
    })();

    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [keyFormData, setKeyFormData] = useState({
        name: '',
        description: '',
        rateLimit: 1000,
        expiresAt: null
    });
    const [showKey, setShowKey] = useState({});
    const [deletingKeyId, setDeletingKeyId] = useState(null);
    const [deletingServiceFilename, setDeletingServiceFilename] = useState(null);
    const [deletingRouteId, setDeletingRouteId] = useState(null);
    const [newServiceFilename, setNewServiceFilename] = useState('');

    // Rate limit tiers
    const rateLimitTiers = [
        { value: 100, label: 'Basic (100 req/hour)' },
        { value: 1000, label: 'Standard (1,000 req/hour)' },
        { value: 10000, label: 'Premium (10,000 req/hour)' },
        { value: 100000, label: 'Enterprise (100,000 req/hour)' }
    ];

    const handleCreateApiKey = async () => {
        try {
            const newKey = await createApiKey({
                projectName,
                user_token: userToken || undefined,
                name: keyFormData.name,
                description: keyFormData.description,
                scopes: [],
                rateLimit: keyFormData.rateLimit,
                expiresAt: keyFormData.expiresAt
            }).unwrap();
            setShowCreateDialog(false);
            const keyPayload = { name: keyFormData.name, description: keyFormData.description, rateLimit: keyFormData.rateLimit, expiresAt: keyFormData.expiresAt };
            setKeyFormData({ name: '', description: '', rateLimit: 1000, expiresAt: null });
            if (newKey?.key) setLastCreatedApiKey({ id: newKey.id, name: newKey.name || keyPayload.name, key: newKey.key });
            dispatch(openSnackbar({ open: true, message: 'API key created successfully', variant: 'alert', alert: { color: 'success' } }));
            refetchApiKeys();
            refetchMetrics();
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.data?.detail || 'Failed to create API key',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    const handleDeleteApiKey = async (keyId) => {
        setDeletingKeyId(keyId);
        try {
            await deleteApiKey({ projectName, keyId, user_token: userToken || undefined }).unwrap();
            dispatch(openSnackbar({ open: true, message: 'API key deleted successfully', variant: 'alert', alert: { color: 'success' } }));
            refetchApiKeys();
            refetchMetrics();
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.data?.detail || 'Failed to delete API key',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        } finally {
            setDeletingKeyId(null);
        }
    };

    const handleRefresh = () => {
        refetchApiKeys();
        refetchMetrics();
        refetchServices();
        refetchRoutes();
        refetchDeploy();
    };

    const services = servicesData?.services ?? [];
    const defaultServiceFile = services.some((s) => s.name === 'sample_api.py') ? 'sample_api.py' : (services[0]?.name || '');

    // Mẫu theo method: path, entrypoint, service_file
    const getDefaultRouteFormForMethod = (method) => {
        const m = (method || 'POST').toUpperCase();
        const entrypoint = `run_${m.toLowerCase()}`;
        const path = `/my/${m.toLowerCase()}`;
        return { path, entrypoint, service_file: defaultServiceFile };
    };

    // Mẫu theo loại endpoint đặc biệt (upload, download) — path, method, entrypoint, service_file
    const getDefaultRouteFormForActionType = (actionType) => {
        if (actionType === 'upload') {
            return { path: '/upload', method: 'POST', entrypoint: 'run_upload', service_file: defaultServiceFile };
        }
        if (actionType === 'download') {
            return { path: '/download', method: 'GET', entrypoint: 'run_download', service_file: defaultServiceFile };
        }
        return getDefaultRouteFormForMethod('POST');
    };

    // Params mẫu (dummy) cho Test tab: ưu tiên params_example của endpoint, không thì theo method/action_type
    const getDefaultParamsForEndpoint = (ep) => {
        if (!ep) return '{}';
        const raw = (ep.params_example || '').trim();
        if (raw) {
            try {
                JSON.parse(raw);
                return raw;
            } catch (_) {}
        }
        const method = (ep.method || 'POST').toUpperCase();
        const at = ep.action_type || (ep.path || '').toLowerCase().includes('upload') ? 'upload' : (ep.path || '').toLowerCase().includes('download') ? 'download' : 'standard';
        if (at === 'upload') return '{}';
        if (at === 'download') return '{"id": "1"}';
        if (method === 'GET') return '{"limit": 10, "skip": 0}';
        if (method === 'DELETE') return '{"id": "1"}';
        if (method === 'POST') return '{"name": "Item", "value": 100}';
        if (method === 'PUT') return '{"id": "1", "name": "Updated", "value": 200}';
        if (method === 'PATCH') return '{"id": "1", "value": 200}';
        return '{}';
    };

    const handleSeedSamples = async () => {
        try {
            const res = await seedSamples(projectName).unwrap();
            const detail = res?.service_file ? `Service ${res.service_file} + ${(res?.routes_added?.length ?? 0)} endpoints` : '';
            dispatch(openSnackbar({ open: true, message: detail ? `${res?.message || 'Đã tạo samples'} (${detail})` : (res?.message || 'Đã tạo samples'), variant: 'alert', alert: { color: 'success' } }));
            refetchServices();
            refetchRoutes();
        } catch (err) {
            dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi tạo samples', variant: 'alert', alert: { color: 'error' } }));
        }
    };

    const copyToClipboard = async (text) => {
        const str = text != null ? String(text) : '';
        if (!str) {
            dispatch(openSnackbar({ open: true, message: 'Không có nội dung để copy', variant: 'alert', alert: { color: 'warning' } }));
            return;
        }
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                await navigator.clipboard.writeText(str);
                dispatch(openSnackbar({ open: true, message: 'Đã copy vào clipboard', variant: 'alert', alert: { color: 'success' } }));
                return;
            }
        } catch (_) {
            // Fallback below
        }
        const ta = document.createElement('textarea');
        ta.value = str;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;boxShadow:none;background:transparent;opacity:0;z-index:-1;';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, str.length);
        let ok = false;
        try {
            ok = document.execCommand('copy');
        } finally {
            document.body.removeChild(ta);
        }
        if (ok) {
            dispatch(openSnackbar({ open: true, message: 'Đã copy vào clipboard', variant: 'alert', alert: { color: 'success' } }));
        } else {
            dispatch(openSnackbar({ open: true, message: 'Copy thất bại (thử chọn text trong ô rồi Ctrl+C)', variant: 'alert', alert: { color: 'error' } }));
        }
    };

    const toggleKeyVisibility = (keyId) => {
        setShowKey((prev) => ({
            ...prev,
            [keyId]: !prev[keyId]
        }));
    };

    const formatKey = (key, show) => {
        if (!key || typeof key !== 'string') return '—';
        if (show) return key;
        if (key.length <= 16) return key;
        return key.slice(0, 8) + '••••••••' + key.slice(-8);
    };

    return (
        <MainCard
            title={
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <IKey size={22} style={{ color: theme.palette.text.secondary }} />
                    <Typography variant="h3">API Gateway</Typography>
                    <Typography variant="body1" color="text.secondary">— {projectSubname}</Typography>
                    <Chip
                        label={projectName}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: 'divider', color: 'text.secondary' }}
                    />
                </Box>
            }
            secondary={
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    {onClose && (
                        <Button variant="outlined" onClick={handleBack} size="small" sx={{ borderColor: 'divider', color: 'text.secondary' }}>
                            Trở về
                        </Button>
                    )}
                    <Button variant="contained" startIcon={<IPlus />} onClick={() => setShowCreateDialog(true)} size="small">
                        Tạo API Key
                    </Button>
                    <IconButton onClick={handleRefresh} disabled={loadingApiKeys || loadingServices || loadingRoutes || loadingDeploy} title="Làm mới" size="small" sx={{ color: 'text.secondary' }}>
                        <IRefresh size={20} />
                    </IconButton>
                </Box>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert
                    severity="info"
                    sx={{
                        bgcolor: 'action.hover',
                        color: 'text.primary',
                        '& .MuiAlert-icon': { color: 'info.main' }
                    }}
                >
                    API Hub: đăng ký Services (function từ Jupyter), tạo Endpoints, Test rồi Deploy. Bên thứ ba gọi URL:port với API Key.
                </Alert>
                <Tabs
                    value={tabValue}
                    onChange={(e, v) => setTabValue(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 44 } }}
                >
                    <Tab label="API Keys" icon={<IKey size={18} />} iconPosition="start" />
                    <Tab label="Services" icon={<IFileCode size={18} />} iconPosition="start" />
                    <Tab label="Endpoints" icon={<IRoute size={18} />} iconPosition="start" />
                    <Tab label="Test" icon={<IPlayerPlay size={18} />} iconPosition="start" />
                    <Tab label="Deploy" icon={<IRocket size={18} />} iconPosition="start" />
                </Tabs>

            {tabValue === 0 && (
            <>
            <Alert severity="info" sx={{ mb: 2 }}>
                Thống kê sử dụng API được ghi nhận khi có request gọi qua <strong>deployed API</strong> (header <code>X-API-Key</code>). Container gateway gửi log về backend và lưu vào cơ sở dữ liệu. Cần cấu hình <code>APIHUB_LOG_SECRET</code> khi deploy để bật ghi nhận.
            </Alert>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {loadingMetrics ? (
                    <>
                        <Skeleton variant="rounded" width={180} height={100} sx={{ flex: '1 1 180px', minWidth: 0 }} />
                        <Skeleton variant="rounded" width={180} height={100} sx={{ flex: '1 1 180px', minWidth: 0 }} />
                        <Skeleton variant="rounded" width={180} height={100} sx={{ flex: '1 1 180px', minWidth: 0 }} />
                        <Skeleton variant="rounded" width={180} height={100} sx={{ flex: '1 1 180px', minWidth: 0 }} />
                        <Skeleton variant="rounded" width={180} height={100} sx={{ flex: '1 1 180px', minWidth: 0 }} />
                        <Skeleton variant="rounded" width={180} height={100} sx={{ flex: '1 1 180px', minWidth: 0 }} />
                    </>
                ) : (
                <>
                <Card variant="outlined" sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Requests (24h)</Typography>
                                <Typography variant="h5">{projectMetrics.totalRequests.toLocaleString()}</Typography>
                            </Box>
                            <IChartLine size={28} style={{ color: theme.palette.text.secondary, opacity: 0.8 }} />
                        </Box>
                    </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Requests (7 ngày)</Typography>
                                <Typography variant="h5">{projectMetrics.requestsLast7d.toLocaleString()}</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Requests (30 ngày)</Typography>
                                <Typography variant="h5">{projectMetrics.requestsLast30d.toLocaleString()}</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                                <Typography variant="h5">{projectMetrics.successRate}%</Typography>
                            </Box>
                            <ISettings size={28} style={{ color: theme.palette.text.secondary, opacity: 0.8 }} />
                        </Box>
                    </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Error Rate</Typography>
                                <Typography variant="h5" color={projectMetrics.errorRate > 5 ? 'error.main' : 'text.primary'}>{projectMetrics.errorRate}%</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Avg Response Time</Typography>
                                <Typography variant="h5">{projectMetrics.avgResponseTime}ms</Typography>
                            </Box>
                            <IWebhook size={28} style={{ color: theme.palette.text.secondary, opacity: 0.8 }} />
                        </Box>
                    </CardContent>
                </Card>
                <Card variant="outlined" sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Active Keys</Typography>
                                <Typography variant="h5">{projectMetrics.activeKeys}</Typography>
                            </Box>
                            <IKey size={28} style={{ color: theme.palette.text.secondary, opacity: 0.8 }} />
                        </Box>
                    </CardContent>
                </Card>
                </>
                )}
            </Box>
            {!loadingMetrics && Array.isArray(projectMetrics.topEndpoints) && projectMetrics.topEndpoints.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Top endpoints (30 ngày)</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Endpoint</TableCell>
                                        <TableCell align="right">Số request</TableCell>
                                        <TableCell align="right">Tỷ lệ</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {projectMetrics.topEndpoints.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{row.endpoint}</TableCell>
                                            <TableCell align="right">{row.requests?.toLocaleString?.() ?? row.requests}</TableCell>
                                            <TableCell align="right">{row.percentage != null ? `${row.percentage}%` : '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6">Project API Keys</Typography>
                        <Typography variant="body2" color="text.secondary">Quản lý API key cho project</Typography>
                    </Box>
                    {loadingApiKeys ? (
                        <Box sx={{ py: 2 }}>
                            <Skeleton variant="rectangular" height={120} sx={{ mb: 1, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={120} sx={{ mb: 1, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                        </Box>
                    ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>API Key</TableCell>
                                    <TableCell>Rate Limit</TableCell>
                                    <TableCell>Usage (24h)</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apiKeys.map((key) => (
                                    <TableRow key={key.id}>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="subtitle2">{key.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {key.description}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                                                    {formatKey(key.key, showKey[key.id])}
                                                </Typography>
                                                <IconButton size="small" onClick={() => toggleKeyVisibility(key.id)} title={showKey[key.id] ? 'Ẩn key' : 'Hiện key đầy đủ'}>
                                                    {showKey[key.id] ? <IEyeOff size={16} /> : <IEye size={16} />}
                                                </IconButton>
                                                <IconButton size="small" onClick={() => copyToClipboard(key.key)} title="Copy key">
                                                    <ICopy size={16} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{key.rateLimit.toLocaleString()}/hour</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">{key.usage?.requests24h ?? 0} requests</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {key.usage?.remainingQuota ?? '—'} remaining
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={key.status}
                                                size="small"
                                                variant="outlined"
                                                color={key.status === 'active' ? 'success' : undefined}
                                                sx={key.status !== 'active' ? { borderColor: 'divider', color: 'text.secondary' } : {}}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={0.5} alignItems="center">
                                                <IconButton size="small" onClick={() => setSelectedKey(key)} sx={{ color: 'text.secondary' }}>
                                                    <IEdit size={16} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteApiKey(key.id)}
                                                    disabled={deleting}
                                                    sx={{ color: 'text.secondary' }}
                                                    title="Xóa API Key"
                                                >
                                                    {deleting && deletingKeyId === key.id ? <CircularProgress size={16} /> : <ITrash size={16} />}
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Tạo API Key mới — {projectSubname}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            fullWidth
                            label="Tên API Key"
                            value={keyFormData.name}
                            onChange={(e) => setKeyFormData({ ...keyFormData, name: e.target.value })}
                            placeholder="vd. Data Reader Key"
                        />
                        <TextField
                            fullWidth
                            label="Mô tả"
                            multiline
                            rows={2}
                            value={keyFormData.description}
                            onChange={(e) => setKeyFormData({ ...keyFormData, description: e.target.value })}
                            placeholder="Mục đích sử dụng key"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Rate Limit</InputLabel>
                            <Select
                                value={keyFormData.rateLimit}
                                label="Rate Limit"
                                onChange={(e) => setKeyFormData({ ...keyFormData, rateLimit: e.target.value })}
                            >
                                {rateLimitTiers.map((tier) => (
                                    <MenuItem key={tier.value} value={tier.value}>
                                        {tier.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Alert severity="info" sx={{ bgcolor: 'action.hover' }}>
                            <Typography variant="body2">
                                API key chỉ áp dụng cho project &quot;{projectSubname}&quot;, không truy cập project khác.
                            </Typography>
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setShowCreateDialog(false)} sx={{ color: 'text.secondary' }}>Hủy</Button>
                    <Button
                        onClick={handleCreateApiKey}
                        variant="contained"
                        disabled={!keyFormData.name || creating}
                        startIcon={creating ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {creating ? 'Đang tạo...' : 'Tạo API Key'}
                    </Button>
                </DialogActions>
            </Dialog>
            </>
            )}

            {tabValue === 1 && (
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6">Services (apihub/services)</Typography>
                        <Typography variant="body2" color="text.secondary">
                            File .py chứa function (vd. run(params)). Mỗi endpoint (tab Endpoints) trỏ tới một service file + entrypoint. &quot;Tạo samples (service + endpoints)&quot; ở tab Endpoints tạo sẵn <strong>sample_api.py</strong> và 8 endpoints dùng chung service này. Chỉnh service trong Monaco, lưu lại; backend lưu version.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        <input
                            accept=".py"
                            type="file"
                            id="upload-service"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f || !projectName) return;
                                try {
                                    await uploadService({ projectName, file: f }).unwrap();
                                    dispatch(openSnackbar({ open: true, message: 'Đã thêm service', variant: 'alert', alert: { color: 'success' } }));
                                    refetchServices();
                                } catch (err) {
                                    dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi upload', variant: 'alert', alert: { color: 'error' } }));
                                }
                                e.target.value = '';
                            }}
                        />
                        <Button variant="outlined" component="label" htmlFor="upload-service" startIcon={uploadingService ? <CircularProgress size={18} /> : <IPlus />} disabled={uploadingService} sx={{ borderColor: 'divider' }}>
                            Upload file .py
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={creatingFromTemplate ? <CircularProgress size={18} /> : <IFileCode size={18} />}
                            disabled={creatingFromTemplate}
                            onClick={() => { setCreateServiceTemplate('POST'); setCreateServiceFilename(''); setCreateServiceDialogOpen(true); }}
                            sx={{ borderColor: 'divider' }}
                            title="Tạo file .py từ template (GET/POST/Upload/Download...) rồi chỉnh sửa. Tạo xong sẽ validate."
                        >
                            Tạo service từ template
                        </Button>
                    </Box>
                    <Dialog open={createServiceDialogOpen} onClose={() => setCreateServiceDialogOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Tạo service từ template</DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Template</InputLabel>
                                    <Select
                                        value={createServiceTemplate}
                                        label="Template"
                                        onChange={(e) => setCreateServiceTemplate(e.target.value)}
                                    >
                                        <MenuItem value="GET">Chuẩn GET (run_get)</MenuItem>
                                        <MenuItem value="POST">Chuẩn POST (run_post)</MenuItem>
                                        <MenuItem value="PUT">Chuẩn PUT (run_put)</MenuItem>
                                        <MenuItem value="PATCH">Chuẩn PATCH (run_patch)</MenuItem>
                                        <MenuItem value="DELETE">Chuẩn DELETE (run_delete)</MenuItem>
                                        <MenuItem value="UPLOAD">Upload file / video (run_upload)</MenuItem>
                                        <MenuItem value="DOWNLOAD">Download file (run_download)</MenuItem>
                                    </Select>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Tạo file .py với function mẫu theo template. Sau khi tạo sẽ validate cú pháp và entrypoint.
                                    </Typography>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    label="Tên file"
                                    value={createServiceFilename}
                                    onChange={(e) => setCreateServiceFilename(e.target.value)}
                                    placeholder="my_api.py"
                                    helperText="Không cần ghi .py, sẽ tự thêm"
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={() => setCreateServiceDialogOpen(false)} sx={{ color: 'text.secondary' }}>Hủy</Button>
                            <Button
                                variant="contained"
                                disabled={creatingFromTemplate}
                                startIcon={creatingFromTemplate ? <CircularProgress size={18} /> : null}
                                onClick={async () => {
                                    const name = (createServiceFilename || '').trim() || 'handler';
                                    if (!projectName) return;
                                    try {
                                        const res = await createServiceFromTemplate({
                                            projectName,
                                            filename: name,
                                            method: createServiceTemplate
                                        }).unwrap();
                                        setCreateServiceDialogOpen(false);
                                        setCreateServiceFilename('');
                                        refetchServices();
                                        const v = res?.validation;
                                        if (v && !v.valid && (v.errors || []).length > 0) {
                                            dispatch(openSnackbar({ open: true, message: `Đã tạo ${res?.name} nhưng validation có lỗi: ${v.errors.join('; ')}`, variant: 'alert', alert: { color: 'warning' } }));
                                        } else {
                                            dispatch(openSnackbar({ open: true, message: `Đã tạo service ${res?.name}${v?.valid ? ' (validation OK)' : ''}`, variant: 'alert', alert: { color: 'success' } }));
                                        }
                                    } catch (err) {
                                        dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi tạo service', variant: 'alert', alert: { color: 'error' } }));
                                    }
                                }}
                            >
                                Tạo
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {loadingServices ? (
                        <Box sx={{ py: 2 }}>
                            <Skeleton variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                        </Box>
                    ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tên file</TableCell>
                                    <TableCell align="right">Thao tác</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(servicesData?.services ?? []).map((s) => (
                                    <TableRow key={s.name}>
                                        <TableCell>{s.name}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => { setServiceEditorFile(s.name); setServiceEditorContent(''); }} sx={{ color: 'text.secondary' }} title="Sửa bằng Monaco">
                                                <IEdit size={18} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                disabled={deletingService}
                                                onClick={async () => {
                                                    if (!window.confirm('Xóa service này?')) return;
                                                    setDeletingServiceFilename(s.name);
                                                    try {
                                                        await deleteService({ projectName, filename: s.name }).unwrap();
                                                        dispatch(openSnackbar({ open: true, message: 'Đã xóa', variant: 'alert', alert: { color: 'success' } }));
                                                        refetchServices();
                                                        refetchRoutes();
                                                        if (serviceEditorFile === s.name) setServiceEditorFile(null);
                                                    } catch (err) {
                                                        dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi', variant: 'alert', alert: { color: 'error' } }));
                                                    } finally {
                                                        setDeletingServiceFilename(null);
                                                    }
                                                }}
                                                sx={{ color: 'text.secondary' }}
                                                title="Xóa service"
                                            >
                                                {deletingService && deletingServiceFilename === s.name ? <CircularProgress size={18} /> : <ITrash size={18} />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    )}
                </CardContent>
            </Card>
            )}

            {/* Dialog Monaco: sửa nội dung service .py */}
            <Dialog open={!!serviceEditorFile} onClose={() => setServiceEditorFile(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '85vh' } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IFileCode size={22} />
                    {serviceEditorFile}
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
                    {loadingServiceContent ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 300 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <MonacoEditor
                            height="100%"
                            language="python"
                            theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
                            value={serviceEditorContent}
                            onChange={setServiceEditorContent}
                            loading={null}
                            options={{ minimap: { enabled: true }, fontSize: 14, wordWrap: 'on' }}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setServiceEditorFile(null)} sx={{ color: 'text.secondary' }}>Đóng</Button>
                    <Button variant="contained" startIcon={savingServiceContent ? <CircularProgress size={18} /> : <IDeviceFloppy size={18} />} disabled={savingServiceContent} onClick={async () => {
                        if (!projectName || !serviceEditorFile) return;
                        try {
                            await updateApihubServiceContent({ projectName, filename: serviceEditorFile, content: serviceEditorContent }).unwrap();
                            dispatch(openSnackbar({ open: true, message: 'Đã lưu service', variant: 'alert', alert: { color: 'success' } }));
                            setServiceEditorFile(null);
                        } catch (err) {
                            dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi lưu', variant: 'alert', alert: { color: 'error' } }));
                        }
                    }}>
                        Lưu
                    </Button>
                </DialogActions>
            </Dialog>

            {tabValue === 2 && (
            <Card variant="outlined">
                <CardContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Endpoints luôn đi kèm Service</Typography>
                        <Typography variant="body2" component="span">
                            Mỗi endpoint gắn với một <strong>service file</strong> (apihub/services) và <strong>entrypoint</strong> (function trong file). &quot;Tạo samples (service + endpoints)&quot; tạo đồng thời: <strong>1 service</strong> (sample_api.py) chứa run_get, run_post, run_put, run_patch, run_delete, run_form, run_upload, run_download; <strong>data</strong> (sample_items.json, uploads); và <strong>8 endpoints</strong> trỏ tới các entrypoint đó. Các tính năng (GET/POST/upload/download...) đều có service kèm theo.
                        </Typography>
                    </Alert>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                        <Box>
                            <Typography variant="h6">Endpoints (Routes)</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Gắn path + method với service (file + entrypoint). Sửa bảng hoặc sửa JSON trực tiếp bằng Monaco.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={seedingSamples ? <CircularProgress size={18} /> : <IFileCode size={18} />}
                                onClick={handleSeedSamples}
                                disabled={seedingSamples}
                                sx={{ borderColor: 'divider' }}
                                title="Tạo service mẫu (sample_api.py) + data (sample_items.json, uploads) + 8 endpoints. Mỗi tính năng (GET/POST/upload/download...) có entrypoint tương ứng trong service."
                            >
                                Tạo samples (service + endpoints)
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={syncingSamples ? <CircularProgress size={18} /> : <IRefresh size={18} />}
                                onClick={async () => {
                                    if (!projectName) return;
                                    try {
                                        const res = await syncSamples(projectName).unwrap();
                                        dispatch(openSnackbar({
                                            open: true,
                                            message: `Đã đồng bộ vào DB: ${res.items_synced ?? 0} items, ${res.files_synced ?? 0} files`,
                                            variant: 'alert',
                                            alert: { color: 'success' }
                                        }));
                                    } catch (err) {
                                        dispatch(openSnackbar({
                                            open: true,
                                            message: err?.data?.detail || 'Đồng bộ thất bại',
                                            variant: 'alert',
                                            alert: { color: 'error' }
                                        }));
                                    }
                                }}
                                disabled={syncingSamples}
                                sx={{ borderColor: 'divider' }}
                                title="Đồng bộ sample_items.json và sample_uploads.json vào DB (apihub_sample_items, apihub_sample_files)"
                            >
                                Đồng bộ vào DB
                            </Button>
                            <Button variant="outlined" startIcon={<IFileCode size={18} />} onClick={() => setRoutesJsonOpen(true)} sx={{ borderColor: 'divider' }}>
                                Sửa JSON
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<IPlus />}
                                onClick={() => {
                                    const method = 'POST';
                                    const defaults = getDefaultRouteFormForMethod(method);
                                    setRouteForm({
                                        path: defaults.path,
                                        method,
                                        service_file: defaults.service_file,
                                        entrypoint: defaults.entrypoint,
                                        param_mapping: {},
                                        enabled: true,
                                        action_type: 'standard',
                                        params_example: '{"name": "Item", "value": 100}'
                                    });
                                    setRouteDialog({ open: true, edit: null });
                                }}
                                sx={{ borderColor: 'divider' }}
                            >
                                Thêm endpoint
                            </Button>
                        </Box>
                    </Box>
                    {loadingRoutes ? (
                        <Box sx={{ py: 2 }}>
                            <Skeleton variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
                        </Box>
                    ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Path</TableCell>
                                    <TableCell>Method</TableCell>
                                    <TableCell>Service</TableCell>
                                    <TableCell>Entrypoint</TableCell>
                                    <TableCell>Bật</TableCell>
                                    <TableCell align="right">Thao tác</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(routesData?.endpoints ?? []).map((ep) => (
                                    <TableRow key={ep.id}>
                                        <TableCell>{ep.path}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {ep.method}
                                            {(() => {
                                                const at = ep.action_type || ((ep.path || '').toLowerCase().includes('upload') || (ep.entrypoint || '').toLowerCase() === 'run_upload' ? 'upload' : ((ep.path || '').toLowerCase().includes('download') || (ep.entrypoint || '').toLowerCase() === 'run_download' ? 'download' : null));
                                                return at === 'upload' || at === 'download' ? (
                                                    <Chip size="small" label={at === 'upload' ? 'Upload' : 'Download'} sx={{ fontSize: '0.7rem', height: 20 }} color={at === 'upload' ? 'secondary' : 'info'} variant="outlined" />
                                                ) : null;
                                            })()}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{ep.service_file}</TableCell>
                                        <TableCell>{ep.entrypoint}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={ep.enabled !== false ? 'Bật' : 'Tắt'}
                                                variant="outlined"
                                                color={ep.enabled !== false ? 'success' : undefined}
                                                sx={ep.enabled === false ? { borderColor: 'divider', color: 'text.secondary' } : {}}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box component="span" sx={{ display: 'inline-flex', gap: 0.5 }}>
                                                <IconButton size="small" onClick={() => { setRouteForm({ path: ep.path, method: ep.method, service_file: ep.service_file, entrypoint: ep.entrypoint || 'run', param_mapping: ep.param_mapping || {}, enabled: ep.enabled !== false, action_type: ep.action_type === 'upload' || ep.action_type === 'download' ? ep.action_type : 'standard', params_example: ep.params_example || '' }); setRouteDialog({ open: true, edit: ep }); }} sx={{ color: 'text.secondary' }}>
                                                    <IEdit size={18} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    disabled={deletingRoute}
                                                    onClick={async () => {
                                                        if (!window.confirm('Xóa endpoint?')) return;
                                                        setDeletingRouteId(ep.id);
                                                        try {
                                                            await deleteRoute({ projectName, routeId: ep.id }).unwrap();
                                                            dispatch(openSnackbar({ open: true, message: 'Đã xóa', variant: 'alert', alert: { color: 'success' } }));
                                                            refetchRoutes();
                                                        } catch (err) {
                                                            dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi', variant: 'alert', alert: { color: 'error' } }));
                                                        } finally {
                                                            setDeletingRouteId(null);
                                                        }
                                                    }}
                                                    sx={{ color: 'text.secondary' }}
                                                    title="Xóa endpoint"
                                                >
                                                    {deletingRoute && deletingRouteId === ep.id ? <CircularProgress size={18} /> : <ITrash size={18} />}
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    )}
                    <Dialog
                        open={routeDialog.open}
                        onClose={() => { setRouteDialog({ open: false, edit: null }); setNewServiceFilename(''); }}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>{routeDialog.edit ? 'Sửa endpoint' : 'Thêm endpoint'}</DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Loại endpoint</InputLabel>
                                    <Select
                                        value={routeForm.action_type || 'standard'}
                                        label="Loại endpoint"
                                        onChange={(e) => {
                                            const actionType = e.target.value;
                                            const defaults = getDefaultRouteFormForActionType(actionType);
                                            const paramsExample = actionType === 'upload' ? '{}' : actionType === 'download' ? '{"id": "1"}' : (defaults.method === 'GET' ? '{"limit": 10, "skip": 0}' : defaults.method === 'DELETE' ? '{"id": "1"}' : '{"name": "Item", "value": 100}');
                                            setRouteForm((prev) => ({
                                                ...prev,
                                                action_type: actionType,
                                                path: defaults.path,
                                                method: defaults.method,
                                                entrypoint: defaults.entrypoint,
                                                service_file: defaults.service_file || prev.service_file,
                                                params_example: prev.params_example || paramsExample
                                            }));
                                        }}
                                    >
                                        <MenuItem value="standard">Chuẩn (JSON / form)</MenuItem>
                                        <MenuItem value="upload">Upload file / video</MenuItem>
                                        <MenuItem value="download">Download file</MenuItem>
                                    </Select>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        Upload: multipart file; Download: response trả file. Tab Test sẽ hiển thị chọn file hoặc lưu file theo loại.
                                    </Typography>
                                </FormControl>
                                <TextField fullWidth label="Path" value={routeForm.path} onChange={(e) => setRouteForm({ ...routeForm, path: e.target.value })} placeholder="/predict" />
                                <FormControl fullWidth>
                                    <InputLabel>Method</InputLabel>
                                    <Select
                                        value={routeForm.method}
                                        label="Method"
                                        onChange={(e) => {
                                            const method = e.target.value;
                                            if (routeForm.action_type === 'upload' || routeForm.action_type === 'download') {
                                                setRouteForm((prev) => ({ ...prev, method }));
                                                return;
                                            }
                                            const defaults = getDefaultRouteFormForMethod(method);
                                            setRouteForm((prev) => ({
                                                ...prev,
                                                method,
                                                path: defaults.path,
                                                entrypoint: defaults.entrypoint,
                                                service_file: defaults.service_file || prev.service_file
                                            }));
                                        }}
                                    >
                                        <MenuItem value="GET">GET</MenuItem>
                                        <MenuItem value="POST">POST</MenuItem>
                                        <MenuItem value="PUT">PUT</MenuItem>
                                        <MenuItem value="PATCH">PATCH</MenuItem>
                                        <MenuItem value="DELETE">DELETE</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel>Service file</InputLabel>
                                    <Select
                                        value={routeForm.service_file}
                                        label="Service file"
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setRouteForm({ ...routeForm, service_file: v });
                                            if (v !== '__new__') setNewServiceFilename('');
                                        }}
                                    >
                                        {(servicesData?.services ?? []).map((s) => (
                                            <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>
                                        ))}
                                        <MenuItem value="__new__">+ Tạo file mới (theo method)</MenuItem>
                                    </Select>
                                </FormControl>
                                {routeForm.service_file === '__new__' && (
                                    <TextField
                                        fullWidth
                                        label="Tên file service mới"
                                        value={newServiceFilename}
                                        onChange={(e) => setNewServiceFilename(e.target.value)}
                                        placeholder="my_handlers.py"
                                        helperText="Sẽ tạo file với mẫu function theo method đã chọn"
                                    />
                                )}
                                <TextField fullWidth label="Entrypoint" value={routeForm.entrypoint} onChange={(e) => setRouteForm({ ...routeForm, entrypoint: e.target.value })} placeholder="run" />
                                <TextField
                                    fullWidth
                                    label="Params mẫu (Query / Body)"
                                    value={routeForm.params_example || ''}
                                    onChange={(e) => setRouteForm({ ...routeForm, params_example: e.target.value })}
                                    placeholder='{"limit": 10} hoặc {"name": "value"}'
                                    helperText="JSON mẫu dùng khi Test và làm hướng dẫn cho user khi gọi API (deploy). Hiển thị sẵn ở tab Test."
                                    multiline
                                    minRows={2}
                                    sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                                />
                                <FormControlLabel
                                    control={<Switch checked={routeForm.enabled} onChange={(e) => setRouteForm({ ...routeForm, enabled: e.target.checked })} />}
                                    label="Bật endpoint"
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={() => setRouteDialog({ open: false, edit: null })} sx={{ color: 'text.secondary' }}>Hủy</Button>
                            <Button
                                variant="contained"
                                disabled={
                                    creatingRoute ||
                                    updatingRoute ||
                                    creatingFromTemplate ||
                                    (!routeDialog.edit && routeForm.service_file === '__new__' && !newServiceFilename?.trim())
                                }
                                startIcon={(creatingRoute || updatingRoute || creatingFromTemplate) ? <CircularProgress size={18} /> : null}
                                onClick={async () => {
                                    try {
                                        if (routeDialog.edit) {
                                            await updateRoute({ projectName, routeId: routeDialog.edit.id, body: routeForm }).unwrap();
                                            dispatch(openSnackbar({ open: true, message: 'Đã cập nhật', variant: 'alert', alert: { color: 'success' } }));
                                        } else {
                                            const isNewFile = routeForm.service_file === '__new__';
                                            const rawName = isNewFile ? (newServiceFilename?.trim() || '') : (routeForm.service_file?.trim() || '');
                                            if (!rawName) {
                                                dispatch(openSnackbar({ open: true, message: 'Chọn service file hoặc nhập tên file mới', variant: 'alert', alert: { color: 'error' } }));
                                                return;
                                            }
                                            const resolvedName = rawName.endsWith('.py') ? rawName : rawName + '.py';
                                            const services = servicesData?.services ?? [];
                                            const hasService = !isNewFile && services.some((s) => s.name === resolvedName);
                                            if (resolvedName && !hasService) {
                                                const templateMethod = routeForm.action_type === 'upload' ? 'UPLOAD' : routeForm.action_type === 'download' ? 'DOWNLOAD' : routeForm.method;
                                                await createServiceFromTemplate({
                                                    projectName,
                                                    filename: resolvedName,
                                                    method: templateMethod
                                                }).unwrap();
                                                await refetchServices();
                                            }
                                            await createRoute({
                                                projectName,
                                                body: { ...routeForm, service_file: resolvedName }
                                            }).unwrap();
                                            dispatch(openSnackbar({ open: true, message: 'Đã thêm endpoint', variant: 'alert', alert: { color: 'success' } }));
                                        }
                                        setRouteDialog({ open: false, edit: null });
                                        refetchRoutes();
                                    } catch (err) {
                                        dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi', variant: 'alert', alert: { color: 'error' } }));
                                    }
                                }}
                            >
                                Lưu
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {/* Dialog Monaco: sửa toàn bộ routes dạng JSON */}
                    <Dialog open={routesJsonOpen} onClose={() => setRoutesJsonOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '85vh' } }}>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IRoute size={22} />
                            endpoints.json (Monaco)
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
                            <MonacoEditor
                                height="100%"
                                language="json"
                                theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
                                value={routesJsonContent}
                                onChange={setRoutesJsonContent}
                                options={{ minimap: { enabled: true }, fontSize: 14, wordWrap: 'on' }}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2 }}>
                            <Button onClick={() => setRoutesJsonOpen(false)} sx={{ color: 'text.secondary' }}>Đóng</Button>
                            <Button variant="contained" startIcon={savingRoutesFull ? <CircularProgress size={18} /> : <IDeviceFloppy size={18} />} disabled={savingRoutesFull} onClick={async () => {
                                if (!projectName) return;
                                let endpoints;
                                try { endpoints = JSON.parse(routesJsonContent); } catch (e) {
                                    dispatch(openSnackbar({ open: true, message: 'JSON không hợp lệ', variant: 'alert', alert: { color: 'error' } }));
                                    return;
                                }
                                if (!Array.isArray(endpoints)) {
                                    dispatch(openSnackbar({ open: true, message: 'Phải là mảng endpoints', variant: 'alert', alert: { color: 'error' } }));
                                    return;
                                }
                                try {
                                    await putApihubRoutesFull({ projectName, endpoints }).unwrap();
                                    dispatch(openSnackbar({ open: true, message: 'Đã lưu routes', variant: 'alert', alert: { color: 'success' } }));
                                    setRoutesJsonOpen(false);
                                    refetchRoutes();
                                } catch (err) {
                                    dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi lưu', variant: 'alert', alert: { color: 'error' } }));
                                }
                            }}>
                                Lưu
                            </Button>
                        </DialogActions>
                    </Dialog>
                </CardContent>
            </Card>
            )}

            {tabValue === 3 && (() => {
                const selectedTestEndpoint = (routesData?.endpoints ?? []).find((e) => e.id === testForm.endpoint_id);
                const testMethod = (selectedTestEndpoint?.method || 'POST').toUpperCase();
                const testIsQuery = testMethod === 'GET' || testMethod === 'DELETE';
                const isUploadEndpoint = selectedTestEndpoint && (
                    selectedTestEndpoint.action_type === 'upload' ||
                    (selectedTestEndpoint.path || '').toLowerCase().includes('upload') ||
                    (selectedTestEndpoint.entrypoint || '').toLowerCase() === 'run_upload'
                );
                const isDownloadEndpoint = selectedTestEndpoint && (
                    selectedTestEndpoint.action_type === 'download' ||
                    (selectedTestEndpoint.path || '').toLowerCase().includes('download') ||
                    (selectedTestEndpoint.entrypoint || '').toLowerCase() === 'run_download'
                );
                const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => { const m = reader.result.match(/^data:[^;]+;base64,(.+)$/); resolve(m ? m[1] : reader.result); };
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
                return (
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6">Test endpoint</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Chọn endpoint và API Key. Chạy test qua backend (dùng .venv). Sau refresh vẫn test được: chọn key trong dropdown và bấm test (backend ghi usage theo key_id).
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <FormControl sx={{ minWidth: 220, flex: '1 1 200px' }}>
                                <InputLabel>Endpoint</InputLabel>
                                <Select
                                    value={testForm.endpoint_id}
                                    label="Endpoint"
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        const ep = (routesData?.endpoints ?? []).find((x) => x.id === id);
                                        const defaultParams = getDefaultParamsForEndpoint(ep);
                                        setTestForm((prev) => ({
                                            ...prev,
                                            endpoint_id: id,
                                            params: defaultParams
                                        }));
                                        const isUpload = ep && (ep.action_type === 'upload' || (ep.path || '').toLowerCase().includes('upload') || (ep.entrypoint || '').toLowerCase() === 'run_upload');
                                        if (!isUpload) setTestUploadFiles([]);
                                    }}
                                >
                                    <MenuItem value="">-- Chọn --</MenuItem>
                                    {(routesData?.endpoints ?? []).filter((e) => e.enabled !== false).map((ep) => (
                                        <MenuItem key={ep.id} value={ep.id}>{ep.method} {ep.path} → {ep.service_file}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {testForm.endpoint_id && (
                                <Chip
                                    size="small"
                                    label={isUploadEndpoint ? 'UPLOAD' : testMethod}
                                    sx={{ fontWeight: 600, alignSelf: 'flex-start' }}
                                    color={isUploadEndpoint ? 'secondary' : (testIsQuery ? 'default' : 'primary')}
                                    variant="outlined"
                                />
                            )}
                            <FormControl sx={{ minWidth: 260, flex: '1 1 200px' }}>
                                <InputLabel>API Key</InputLabel>
                                <Select
                                    value={testForm.selected_key_id}
                                    label="API Key"
                                    onChange={(e) => setTestForm({ ...testForm, selected_key_id: e.target.value })}
                                >
                                    <MenuItem value="">-- Chọn API Key --</MenuItem>
                                    {apiKeys.filter((k) => k.status === 'active').map((k) => (
                                        <MenuItem key={k.id} value={k.id}>
                                            {k.name} {k.key && !k.key.includes('••••••••') ? ' ✓' : ` (${k.key})`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {isDownloadEndpoint && (
                                <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 100%' }}>
                                    Endpoint download: dùng query <code>id</code> (vd. id của file đã upload). Nếu response có file_base64, dùng nút &quot;Lưu file&quot; bên dưới kết quả.
                                </Typography>
                            )}
                            {isUploadEndpoint ? (
                                <Box sx={{ flex: '1 1 100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">File upload (file / video / ảnh)</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                        <Button variant="outlined" component="label" size="small" sx={{ borderColor: 'divider' }}>
                                            Chọn file
                                            <input
                                                type="file"
                                                multiple
                                                hidden
                                                accept="*/*"
                                                onChange={(e) => {
                                                    const list = Array.from(e.target.files || []);
                                                    setTestUploadFiles((prev) => [...prev, ...list]);
                                                    e.target.value = '';
                                                }}
                                            />
                                        </Button>
                                        {testUploadFiles.length > 0 && (
                                            <Button size="small" onClick={() => setTestUploadFiles([])} sx={{ color: 'text.secondary' }}>
                                                Xóa hết
                                            </Button>
                                        )}
                                    </Box>
                                    {testUploadFiles.length > 0 && (
                                        <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: '0.875rem', color: 'text.secondary' }}>
                                            {testUploadFiles.map((f, i) => (
                                                <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {f.name} ({(f.size / 1024).toFixed(1)} KB)
                                                    <IconButton size="small" onClick={() => setTestUploadFiles((prev) => prev.filter((_, j) => j !== i))} sx={{ p: 0.25 }}>
                                                        <ITrash size={14} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        Chọn một hoặc nhiều file để gửi lên endpoint upload. Test qua backend sẽ gửi nội dung file dạng base64 trong params.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ flex: '1 1 100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {testForm.endpoint_id && (() => {
                                        const dummyParams = getDefaultParamsForEndpoint(selectedTestEndpoint);
                                        let pretty = '{}';
                                        try { pretty = JSON.stringify(JSON.parse(dummyParams), null, 2); } catch (_) { pretty = dummyParams; }
                                        return (
                                            <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
                                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Query / Body mẫu (hướng dẫn cho endpoint này)</Typography>
                                                <Box component="pre" sx={{ m: 0, p: 1, bgcolor: 'background.paper', borderRadius: 1, fontSize: '0.8rem', fontFamily: 'monospace', overflow: 'auto' }}>
                                                    {pretty}
                                                </Box>
                                                <Button size="small" sx={{ mt: 0.5 }} onClick={() => setTestForm((prev) => ({ ...prev, params: dummyParams }))}>
                                                    Điền params theo mẫu
                                                </Button>
                                            </Alert>
                                        );
                                    })()}
                                    <TextField
                                        fullWidth
                                        sx={{ flex: '1 1 240px' }}
                                        label={testIsQuery ? 'Query params (JSON)' : 'Body (JSON)'}
                                        value={testForm.params}
                                        onChange={(e) => setTestForm({ ...testForm, params: e.target.value })}
                                        placeholder={testIsQuery ? '{"limit": 10, "skip": 0}' : '{"name": "value"}'}
                                        helperText={testIsQuery ? 'GET/DELETE: params gửi dưới dạng query string. Dùng mẫu trên hoặc chỉnh theo API.' : 'POST/PUT/PATCH: params gửi trong request body. Dùng mẫu trên hoặc chỉnh theo API.'}
                                        multiline
                                        minRows={3}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <Button variant="contained" startIcon={testing ? <CircularProgress size={18} /> : <IPlayerPlay />} disabled={!testForm.endpoint_id || !testForm.selected_key_id || testing} onClick={async () => {
                                let params = {};
                                try { params = JSON.parse(testForm.params || '{}'); } catch (_) {}
                                if (isUploadEndpoint && testUploadFiles.length > 0) {
                                    try {
                                        params._files = await Promise.all(testUploadFiles.map(async (f) => ({
                                            filename: f.name,
                                            content_b64: await readFileAsBase64(f),
                                            content_type: f.type || 'application/octet-stream',
                                            size: f.size
                                        })));
                                    } catch (e) {
                                        dispatch(openSnackbar({ open: true, message: 'Đọc file lỗi: ' + (e?.message || e), variant: 'alert', alert: { color: 'error' } }));
                                        return;
                                    }
                                }
                                setTestResult(null);
                                try {
                                    const res = await testEndpoint({
                                        projectName,
                                        body: {
                                            endpoint_id: testForm.endpoint_id,
                                            params,
                                            api_key: effectiveTestApiKey || undefined,
                                            key_id: testForm.selected_key_id || undefined
                                        }
                                    }).unwrap();
                                    setTestResult(res);
                                    dispatch(openSnackbar({ open: true, message: 'Test thành công', variant: 'alert', alert: { color: 'success' } }));
                                } catch (err) {
                                    setTestResult({ success: false, error: err?.data?.detail || err?.message || 'Lỗi' });
                                    dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Test lỗi', variant: 'alert', alert: { color: 'error' } }));
                                }
                            }}>{testing ? 'Đang test...' : 'Chạy test (backend)'}</Button>
                            {deployStatusData?.deployed && (deployStatusData?.base_url || deployStatusData?.url) && (() => {
                                const ep = (routesData?.endpoints ?? []).find((e) => e.id === testForm.endpoint_id);
                                const path = ep?.path || '';
                                const method = (ep?.method || 'POST').toUpperCase();
                                const isUploadDeploy = isUploadEndpoint && testUploadFiles.length > 0;
                                return (
                                    <Button variant="outlined" startIcon={testing ? <CircularProgress size={18} /> : <IPlayerPlay />} disabled={!testForm.endpoint_id || !effectiveTestApiKey || testing || (isUploadEndpoint && testUploadFiles.length === 0)} title={isUploadEndpoint && testUploadFiles.length === 0 ? 'Endpoint upload cần chọn ít nhất 1 file' : (!effectiveTestApiKey ? 'Gọi deployed cần key tạo trong phiên này (full key)' : '')} onClick={async () => {
                                        let params = {};
                                        try { params = JSON.parse(testForm.params || '{}'); } catch (_) {}
                                        setTestResult(null);
                                        const base = deployStatusData.base_url || deployStatusData.url || '';
                                        let url = `${base.replace(/\/$/, '')}${path}`;
                                        const sendParamsInQuery = !isUploadDeploy && (method === 'GET' || method === 'DELETE');
                                        if (sendParamsInQuery && Object.keys(params).length > 0) {
                                            const q = new URLSearchParams(params).toString();
                                            url += (url.includes('?') ? '&' : '?') + q;
                                        }
                                        try {
                                            let body;
                                            let headers = { 'X-API-Key': effectiveTestApiKey };
                                            if (isUploadDeploy) {
                                                const formData = new FormData();
                                                testUploadFiles.forEach((f) => formData.append('file', f));
                                                body = formData;
                                                // Không set Content-Type để browser tự set multipart boundary
                                            } else {
                                                headers['Content-Type'] = 'application/json';
                                                body = !sendParamsInQuery ? JSON.stringify(params) : undefined;
                                            }
                                            const res = await fetch(url, { method, headers, body });
                                            const data = await res.json().catch(() => ({}));
                                            if (res.ok) setTestResult({ success: true, result: data });
                                            else setTestResult({ success: false, error: data?.detail || res.statusText });
                                            dispatch(openSnackbar({ open: true, message: res.ok ? 'Gọi deployed thành công' : (data?.detail || 'Lỗi'), variant: 'alert', alert: { color: res.ok ? 'success' : 'error' } }));
                                        } catch (err) {
                                            setTestResult({ success: false, error: err?.message || 'Lỗi mạng' });
                                            dispatch(openSnackbar({ open: true, message: err?.message || 'Lỗi', variant: 'alert', alert: { color: 'error' } }));
                                        }
                                    }}>Gọi qua deployed URL</Button>
                                );
                            })()}
                        </Box>
                        {testResult && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                                <Typography variant="subtitle2" color="text.secondary">Kết quả</Typography>
                                {isDownloadEndpoint && testResult?.result?.result?.file_base64 && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            try {
                                                const b64 = testResult.result.result.file_base64;
                                                const filename = testResult.result.result.filename || 'download';
                                                const bin = atob(b64);
                                                const bytes = new Uint8Array(bin.length);
                                                for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                                                const blob = new Blob([bytes]);
                                                const a = document.createElement('a');
                                                a.href = URL.createObjectURL(blob);
                                                a.download = filename;
                                                a.click();
                                                URL.revokeObjectURL(a.href);
                                                dispatch(openSnackbar({ open: true, message: 'Đã lưu file', variant: 'alert', alert: { color: 'success' } }));
                                            } catch (e) {
                                                dispatch(openSnackbar({ open: true, message: 'Lưu file lỗi', variant: 'alert', alert: { color: 'error' } }));
                                            }
                                        }}
                                    >
                                        Lưu file
                                    </Button>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: 'action.hover',
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    whiteSpace: 'pre-wrap',
                                    maxHeight: 300,
                                    overflow: 'auto',
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                {typeof testResult.result !== 'undefined' ? JSON.stringify(testResult.result, null, 2) : testResult.error}
                            </Box>
                        </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
            ); })()}

            {tabValue === 4 && (
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6">Deploy API Hub</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Tạo container FastAPI cho project: mount workspace, gán port. Bên thứ ba gọi URL với header X-API-Key.
                        </Typography>
                    </Box>
                    {loadingDeploy ? (
                        <Box sx={{ py: 2 }}>
                            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                            <Skeleton variant="rectangular" height={44} sx={{ mt: 2, borderRadius: 1, maxWidth: 160 }} />
                        </Box>
                    ) : deployStatusData?.deployed ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Alert severity="success" sx={{ bgcolor: 'action.hover' }}>
                                Đã deploy. Container đang chạy.
                            </Alert>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2">
                                    <Typography component="span" color="text.secondary">URL (public): </Typography>
                                    <Typography component="span" fontFamily="monospace">{deployStatusData?.base_url || deployStatusData?.url || '-'}</Typography>
                                    {(deployStatusData?.base_url || deployStatusData?.url) && (
                                        <IconButton size="small" onClick={() => copyToClipboard(deployStatusData?.base_url || deployStatusData?.url)} title="Copy URL">
                                            <ICopy size={16} />
                                        </IconButton>
                                    )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Port: {deployStatusData?.port}</Typography>
                            </Box>
                            {deployStatusData?.test_instructions && (
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                    <Typography variant="body2"><strong>Hướng dẫn test:</strong> {deployStatusData.test_instructions}</Typography>
                                </Alert>
                            )}
                            {Array.isArray(deployStatusData?.deployed_apis) && deployStatusData.deployed_apis.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                        <Typography variant="subtitle2">Danh sách API đã deploy</Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<ICopy size={16} />}
                                            onClick={() => { setDeployCurlTableVisible(false); setDeployCurlSelectedKeyId(''); setDeployCurlFetchedKey(''); setDeployCurlDialogOpen(true); }}
                                            sx={{ borderColor: 'divider' }}
                                        >
                                            Tạo bảng curl (chọn API Key)
                                        </Button>
                                    </Box>
                                    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Method</TableCell>
                                                    <TableCell>Path</TableCell>
                                                    <TableCell>URL</TableCell>
                                                    <TableCell align="right">Copy curl</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {deployStatusData.deployed_apis.map((api, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell><Chip size="small" label={api.method} color="primary" variant="outlined" /></TableCell>
                                                        <TableCell sx={{ fontFamily: 'monospace' }}>{api.path}</TableCell>
                                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }} title={api.url}>{api.url}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton size="small" onClick={() => copyToClipboard(api.curl_example)} title="Copy curl (thay YOUR_KEY)">
                                                                <ICopy size={16} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Dialog open={deployCurlDialogOpen} onClose={() => setDeployCurlDialogOpen(false)} maxWidth="md" fullWidth>
                                        <DialogTitle>Tạo bảng curl với API Key</DialogTitle>
                                        <DialogContent>
                                            {!deployCurlTableVisible ? (
                                                <Box sx={{ pt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                        Chọn API Key để tạo bảng lệnh curl hoàn chỉnh. Nếu chọn &quot;Key vừa tạo&quot;, curl sẽ chứa key thật; key khác chỉ hiển thị YOUR_KEY (thay thủ công).
                                                    </Typography>
                                                    <FormControl fullWidth>
                                                        <InputLabel>API Key</InputLabel>
                                                        <Select
                                                            value={deployCurlSelectedKeyId}
                                                            label="API Key"
                                                            onChange={(e) => setDeployCurlSelectedKeyId(e.target.value)}
                                                        >
                                                            {lastCreatedApiKey && (
                                                                <MenuItem value={lastCreatedApiKey.id}>
                                                                    Key vừa tạo: {lastCreatedApiKey.name} (curl sẽ có key đầy đủ)
                                                                </MenuItem>
                                                            )}
                                                            {apiKeys.filter((k) => k.id !== lastCreatedApiKey?.id).map((k) => (
                                                                <MenuItem key={k.id} value={k.id}>{k.name || k.id} — {k.key || '(key đã ẩn)'}</MenuItem>
                                                            ))}
                                                            {(!apiKeys?.length && !lastCreatedApiKey) && (
                                                                <MenuItem value="" disabled>Chưa có API Key. Tạo key ở tab API Keys.</MenuItem>
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            ) : (
                                                <Box sx={{ pt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Bảng curl dùng key đã chọn. Copy từng dòng hoặc copy tất cả.
                                                    </Typography>
                                                    {(() => {
                                                        const fullKey = (deployCurlSelectedKeyId === lastCreatedApiKey?.id ? lastCreatedApiKey?.key : null) || deployCurlFetchedKey || '';
                                                        const apis = deployStatusData?.deployed_apis || [];
                                                        const rows = apis.map((api) => ({
                                                            ...api,
                                                            curl: fullKey ? (api.curl_example || '').replace('YOUR_KEY', fullKey) : (api.curl_example || ''),
                                                        }));
                                                        const allCurlText = rows.map((r) => r.curl).filter(Boolean).join('\n\n');
                                                        const doCopyAllCurl = () => {
                                                            const text = allCurlText;
                                                            if (!text) {
                                                                dispatch(openSnackbar({ open: true, message: 'Không có nội dung', variant: 'alert', alert: { color: 'warning' } }));
                                                                return;
                                                            }
                                                            if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                                                                navigator.clipboard.writeText(text).then(() => {
                                                                    dispatch(openSnackbar({ open: true, message: 'Đã copy tất cả curl', variant: 'alert', alert: { color: 'success' } }));
                                                                }).catch(() => {
                                                                    copyViaRef(text);
                                                                });
                                                                return;
                                                            }
                                                            copyViaRef(text);
                                                        };
                                                        const copyViaRef = (str, successMsg = 'Đã copy tất cả curl') => {
                                                            const el = deployCurlCopyRef.current;
                                                            if (!el) {
                                                                dispatch(openSnackbar({ open: true, message: 'Copy thất bại', variant: 'alert', alert: { color: 'error' } }));
                                                                return;
                                                            }
                                                            el.value = str;
                                                            el.style.pointerEvents = 'auto';
                                                            el.style.position = 'fixed';
                                                            el.style.left = '0';
                                                            el.style.top = '0';
                                                            el.style.width = '100%';
                                                            el.style.height = '100%';
                                                            el.style.opacity = '0';
                                                            el.style.zIndex = '9999';
                                                            el.focus();
                                                            el.select();
                                                            try {
                                                                const ok = document.execCommand('copy');
                                                                if (ok) dispatch(openSnackbar({ open: true, message: successMsg, variant: 'alert', alert: { color: 'success' } }));
                                                                else dispatch(openSnackbar({ open: true, message: 'Copy thất bại. Chọn text trong ô rồi Ctrl+C', variant: 'alert', alert: { color: 'error' } }));
                                                            } catch (err) {
                                                                dispatch(openSnackbar({ open: true, message: 'Copy thất bại', variant: 'alert', alert: { color: 'error' } }));
                                                            }
                                                            el.style.pointerEvents = 'none';
                                                            el.style.position = 'absolute';
                                                            el.style.left = '-9999px';
                                                            el.style.top = '0';
                                                            el.style.width = '1px';
                                                            el.style.height = '1px';
                                                            el.style.zIndex = '-1';
                                                        };
                                                        return (
                                                            <>
                                                                <textarea
                                                                    ref={deployCurlCopyRef}
                                                                    readOnly
                                                                    aria-hidden="true"
                                                                    tabIndex={-1}
                                                                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none', zIndex: -1 }}
                                                                />
                                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                    Bấm &quot;Copy tất cả curl&quot; để copy chỉ các lệnh curl. Hoặc chọn và copy trong ô bên dưới.
                                                                </Typography>
                                                                <Button
                                                                    type="button"
                                                                    size="small"
                                                                    startIcon={<ICopy size={14} />}
                                                                    sx={{ mb: 1 }}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        doCopyAllCurl();
                                                                    }}
                                                                >
                                                                    Copy tất cả curl
                                                                </Button>
                                                                <TextField
                                                                    fullWidth
                                                                    multiline
                                                                    minRows={6}
                                                                    maxRows={16}
                                                                    value={allCurlText}
                                                                    readOnly
                                                                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                                                                    sx={{ mb: 1, '& .MuiInputBase-root': { bgcolor: 'action.hover' } }}
                                                                    onFocus={(e) => e.target.select()}
                                                                />
                                                                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Method</TableCell>
                                                                                <TableCell>Path</TableCell>
                                                                                <TableCell>Curl (có API Key)</TableCell>
                                                                                <TableCell align="right"></TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {rows.map((row, idx) => (
                                                                                <TableRow key={idx}>
                                                                                    <TableCell><Chip size="small" label={row.method} variant="outlined" /></TableCell>
                                                                                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.path}</TableCell>
                                                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all', maxWidth: 400 }} title={row.curl}>{row.curl}</TableCell>
                                                                                    <TableCell align="right">
                                                                                        <IconButton
                                                                                            type="button"
                                                                                            size="small"
                                                                                            title="Copy dòng"
                                                                                            onClick={(e) => {
                                                                                                e.preventDefault();
                                                                                                e.stopPropagation();
                                                                                                const t = row.curl;
                                                                                                if (navigator.clipboard?.writeText) {
                                                                                                    navigator.clipboard.writeText(t).then(() => dispatch(openSnackbar({ open: true, message: 'Đã copy lệnh curl', variant: 'alert', alert: { color: 'success' } }))).catch(() => copyViaRef(t, 'Đã copy lệnh curl'));
                                                                                                } else { copyViaRef(t, 'Đã copy lệnh curl'); }
                                                                                            }}
                                                                                        >
                                                                                            <ICopy size={16} />
                                                                                        </IconButton>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </>
                                                        );
                                                    })()}
                                                </Box>
                                            )}
                                        </DialogContent>
                                        <DialogActions sx={{ px: 3, pb: 2 }}>
                                            {!deployCurlTableVisible ? (
                                                <>
                                                    <Button onClick={() => setDeployCurlDialogOpen(false)} sx={{ color: 'text.secondary' }}>Hủy</Button>
                                                    <Button
                                                        variant="contained"
                                                        disabled={!deployCurlSelectedKeyId || loadingKeyPlaintext}
                                                        startIcon={loadingKeyPlaintext ? <CircularProgress size={18} /> : null}
                                                        onClick={async () => {
                                                            if (deployCurlSelectedKeyId === lastCreatedApiKey?.id) {
                                                                setDeployCurlFetchedKey('');
                                                                setDeployCurlTableVisible(true);
                                                                return;
                                                            }
                                                            try {
                                                                const res = await fetchKeyForCurl({ projectName, keyId: deployCurlSelectedKeyId }).unwrap();
                                                                setDeployCurlFetchedKey(res?.key || '');
                                                                setDeployCurlTableVisible(true);
                                                            } catch (err) {
                                                                dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Không lấy được key (key cũ có thể không lưu plaintext)', variant: 'alert', alert: { color: 'warning' } }));
                                                                setDeployCurlFetchedKey('');
                                                                setDeployCurlTableVisible(true);
                                                            }
                                                        }}
                                                    >
                                                        {loadingKeyPlaintext ? 'Đang lấy key...' : 'Tạo bảng'}
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button onClick={() => { setDeployCurlTableVisible(false); setDeployCurlSelectedKeyId(''); setDeployCurlFetchedKey(''); }}>Chọn key khác</Button>
                                                    <Button variant="contained" onClick={() => setDeployCurlDialogOpen(false)}>Đóng</Button>
                                                </>
                                            )}
                                        </DialogActions>
                                    </Dialog>
                                </Box>
                            )}
                            <Button
                                variant="outlined"
                                startIcon={undeploying ? <CircularProgress size={18} /> : <ITrash />}
                                disabled={undeploying}
                                onClick={async () => {
                                    try {
                                        await undeploy(projectName).unwrap();
                                        dispatch(openSnackbar({ open: true, message: 'Đã undeploy', variant: 'alert', alert: { color: 'success' } }));
                                        refetchDeploy();
                                    } catch (err) {
                                        dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi', variant: 'alert', alert: { color: 'error' } }));
                                    }
                                }}
                                sx={{ alignSelf: 'flex-start', borderColor: 'divider', color: 'text.secondary' }}
                            >
                                Undeploy
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Alert severity="info" sx={{ bgcolor: 'action.hover' }}>
                                Chưa deploy. Cần build image apihub-gateway:latest trước (docker/build trong repo).
                            </Alert>
                            <Button
                                variant="contained"
                                startIcon={deploying ? <CircularProgress size={18} /> : <IRocket />}
                                disabled={deploying}
                                onClick={async () => {
                                    try {
                                        const res = await deploy(projectName).unwrap();
                                        dispatch(openSnackbar({ open: true, message: res?.message || 'Đã deploy', variant: 'alert', alert: { color: 'success' } }));
                                        refetchDeploy();
                                        refetchMetrics();
                                    } catch (err) {
                                        dispatch(openSnackbar({ open: true, message: err?.data?.detail || 'Lỗi deploy', variant: 'alert', alert: { color: 'error' } }));
                                    }
                                }}
                            >
                                Deploy
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
            )}
            </Box>
        </MainCard>
    );
};

export default ProjectApiGateway;
